import { sortBy, uniqBy } from 'lodash';
import { selectCurrentEmployee } from 'store/App';
import { IEmployerState, IStore } from 'store/types';
import { EmployeeRole, EmployeeType, IEmployerSite, SiteRole } from 'types';
import { transformCurrentEmployeeRole } from 'utils/employers';

export const selectEmployers = (state: IStore) => state.employers;

export const selectCurrentEmployeeOrg = (state: IStore): IEmployerState | undefined => {
  const orgId = selectCurrentEmployee()(state)?.organizationId;
  if (orgId) return state.employers?.[orgId];
  return undefined;
};

export const selectCurrentEmployeeSite = (state: IStore) => {
  const siteId = selectCurrentEmployee()(state)?.siteId;
  if (siteId) {
    const currentOrg = selectCurrentEmployeeOrg(state);
    return currentOrg?.sites?.[siteId];
  }
  return undefined;
};

export const selectCurrentEmployeeAdminSites = (state: IStore): IEmployerSite[] | undefined => {
  const employer = selectCurrentEmployeeOrg(state);
  const roles = [SiteRole.ADMIN];
  return sortBy(
    Object.values(employer?.sites || []).filter((site) => roles.includes(site.role)),
    'name'
  );
};

export const selectCurrentEmployeeManagedSites = (state: IStore): IEmployerSite[] | undefined => {
  const employer = selectCurrentEmployeeOrg(state);
  const roles = [SiteRole.ADMIN, SiteRole.MANAGER];
  return sortBy(
    Object.values(employer?.sites || []).filter((site) => roles.includes(site.role)),
    'name'
  );
};

export const selectSitesForUser = (state: IStore) => {
  const sites: IEmployerSite[] = [];
  const organizations = Object.values(state.employers);
  organizations.forEach((org) =>
    Object.values(org.sites).forEach((site) => {
      sites.push(site);
    })
  );
  return uniqBy(sites, 'id');
};

export const selectCurrentEmployeeType = (state: IStore) => {
  const currentEmployee = selectCurrentEmployee()(state);
  if (!currentEmployee) return undefined;
  return state.employers?.[currentEmployee.organizationId]?.type;
};

export const selectCurrentEmployeeRole = (state: IStore) => {
  const currentEmployee = selectCurrentEmployee()(state);
  if (!currentEmployee) return undefined;

  const currentOrg = state.employers?.[currentEmployee.organizationId];
  if (currentOrg?.type === EmployeeType.ADMIN) return SiteRole.ADMIN;

  return currentOrg?.sites[currentEmployee.siteId]?.role;
};

export const selectIsPermitted = (permittedRoles: Array<EmployeeRole>) => (state: IStore) => {
  const currentEmployeeType = selectCurrentEmployeeType(state);
  const currentEmployeeRole = selectCurrentEmployeeRole(state);

  return Boolean(currentEmployeeRole && permittedRoles.includes(transformCurrentEmployeeRole(currentEmployeeType, currentEmployeeRole)));
};
